import React, { useCallback, useMemo, useState } from 'react';
import {
  Avatar,
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import ConfirmationNumberOutlinedIcon from '@mui/icons-material/ConfirmationNumberOutlined';
import Barcode from 'react-barcode';

import { actionNotifier } from '../../../components/tostify';
import { dateStandard } from '../../../shared/dateFunction';
import { voucherBarCode } from '../../../hooks/utilsFn/utilsFn';

const FONT_FAMILY = '"Be Vietnam Pro", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';

/**
 * Copy to clipboard with navigator fallback
 */
const copyText = async (text) => {
  if (!text) return false;
  try {
    if (navigator?.clipboard?.writeText) {
      await navigator.clipboard.writeText(String(text));
      return true;
    }
  } catch (err) {
    console.warn('Clipboard writeText failed, falling back:', err);
  }

  try {
    const textArea = document.createElement('textarea');
    textArea.value = String(text);
    textArea.style.position = 'fixed';
    textArea.style.opacity = '0';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    const successful = document.execCommand('copy');
    document.body.removeChild(textArea);
    return successful;
  } catch (err) {
    console.error('Fallback copy failed:', err);
    return false;
  }
};

/**
 * Compact Credential Box for Code / PIN with inline copy feedback
 */
const MobileCredentialBox = ({ label, value, type, copiedKey, onCopy }) => {
  if (!value) return null;
  const isCopied = copiedKey === type;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 1,
        px: 1.5,
        py: 0.85,
        borderRadius: 2,
        backgroundColor: '#F8FAFC',
        border: '1px solid #E2E8F0',
        transition: 'all 0.18s ease',
        '&:hover': {
          borderColor: '#CBD5E1',
          backgroundColor: '#F1F5F9',
        },
      }}
    >
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography
          sx={{
            fontSize: 10,
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: 0.6,
            color: '#64748B',
            fontFamily: FONT_FAMILY,
            lineHeight: 1.15,
            mb: 0.2,
          }}
        >
          {label}
        </Typography>
        <Typography
          sx={{
            fontSize: 13.5,
            fontWeight: 700,
            color: '#002255',
            fontFamily: 'monospace, "Be Vietnam Pro"',
            letterSpacing: 0.7,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            userSelect: 'all',
          }}
        >
          {value}
        </Typography>
      </Box>

      <Button
        size="small"
        onClick={() => onCopy(value, label, type)}
        startIcon={
          isCopied ? (
            <CheckRoundedIcon sx={{ fontSize: '14px !important' }} />
          ) : (
            <ContentCopyRoundedIcon sx={{ fontSize: '13px !important' }} />
          )
        }
        sx={{
          flexShrink: 0,
          minWidth: 70,
          height: 28,
          fontSize: 11,
          fontWeight: 600,
          textTransform: 'none',
          borderRadius: 1.5,
          fontFamily: FONT_FAMILY,
          transition: 'all 0.18s ease',
          backgroundColor: isCopied ? 'rgba(22, 163, 74, 0.10)' : '#FFFFFF',
          color: isCopied ? '#15803D' : '#002255',
          border: isCopied ? '1px solid rgba(22, 163, 74, 0.35)' : '1px solid #CBD5E1',
          boxShadow: isCopied ? 'none' : '0 1px 2px rgba(0,0,0,0.03)',
          '&:hover': {
            backgroundColor: isCopied ? 'rgba(22, 163, 74, 0.16)' : '#002255',
            color: isCopied ? '#15803D' : '#FFFFFF',
            borderColor: isCopied ? 'rgba(22, 163, 74, 0.45)' : '#002255',
          },
        }}
      >
        {isCopied ? 'Copied' : 'Copy'}
      </Button>
    </Box>
  );
};

function MobileGiftVoucher(props) {
  const { handleClose, orderData, voucherData, voucherFlag } = props;
  const [copiedKey, setCopiedKey] = useState(null);

  const voucherNo = voucherData?.VoucherNo ?? voucherData?.voucherCode ?? voucherData?.code ?? '';
  const voucherPin = voucherData?.Voucherpin ?? voucherData?.voucherPin ?? voucherData?.pin ?? '';
  const value = voucherData?.Value ?? voucherData?.value ?? orderData?.amount ?? 0;
  const endDate = voucherData?.EndDate ?? voucherData?.expiryDate ?? voucherData?.expire_at ?? null;
  const formattedExpiry = endDate ? dateStandard(endDate) : null;

  const productTitle =
    voucherData?.ProductName ??
    orderData?.product?.title ??
    orderData?.product ??
    'Gift Voucher';

  const productImage =
    orderData?.product?.image ??
    orderData?.productImage ??
    voucherData?.image ??
    null;

  const orderId = orderData?.id ?? voucherData?.order_id ?? null;

  const barcodeVal = useMemo(
    () => (voucherNo || voucherPin ? voucherBarCode(voucherNo || 'N/A', voucherPin || 'N/A', voucherFlag) : null),
    [voucherNo, voucherPin, voucherFlag]
  );

  const handleCopy = useCallback(async (text, label, type) => {
    const success = await copyText(text);
    if (success) {
      setCopiedKey(type);
      actionNotifier.info(`${label} Copied!`, {
        position: 'top-right',
        autoClose: 1200,
        theme: 'dark',
        hideProgressBar: true,
      });
      setTimeout(() => {
        setCopiedKey((prev) => (prev === type ? null : prev));
      }, 1800);
    }
  }, []);

  return (
    <Box
      sx={{
        width: '100%',
        minHeight: '100vh',
        height: '100%',
        backgroundColor: '#F8FAFC',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Top App Bar with Back Navigation */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: { xs: 1.5, sm: 2 },
          py: 1.25,
          backgroundColor: '#FFFFFF',
          borderBottom: '1px solid #E2E8F0',
          position: 'sticky',
          top: 0,
          zIndex: 10,
        }}
      >
        <Stack direction="row" spacing={1.25} alignItems="center" sx={{ minWidth: 0, flex: 1 }}>
          <IconButton
            size="small"
            onClick={handleClose}
            aria-label="Back"
            sx={{
              width: 34,
              height: 34,
              borderRadius: 2,
              color: '#002255',
              backgroundColor: '#F1F5F9',
              border: '1px solid #E2E8F0',
              transition: 'all 0.18s ease',
              '&:hover': {
                backgroundColor: 'rgba(0,34,85,0.08)',
              },
            }}
          >
            <ArrowBackRoundedIcon sx={{ fontSize: 20 }} />
          </IconButton>

          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography
                sx={{
                  fontSize: { xs: 15, sm: 16 },
                  fontWeight: 700,
                  color: '#002255',
                  fontFamily: FONT_FAMILY,
                  lineHeight: 1.2,
                }}
              >
                Gift Voucher
              </Typography>

              {orderId && (
                <Chip
                  size="small"
                  icon={<ReceiptLongOutlinedIcon sx={{ fontSize: '11px !important' }} />}
                  label={`#${orderId}`}
                  sx={{
                    height: 20,
                    fontSize: 10.5,
                    fontWeight: 700,
                    color: '#002255',
                    backgroundColor: 'rgba(0,34,85,0.06)',
                    border: '1px solid rgba(0,34,85,0.12)',
                    '& .MuiChip-icon': { color: '#002255' },
                  }}
                />
              )}
            </Stack>
          </Box>
        </Stack>

        <Tooltip title="Close" arrow>
          <IconButton
            size="small"
            onClick={handleClose}
            aria-label="Close"
            sx={{
              width: 30,
              height: 30,
              borderRadius: 1.75,
              color: '#64748B',
              backgroundColor: '#F1F5F9',
              border: '1px solid #E2E8F0',
              transition: 'all 0.18s ease',
              '&:hover': {
                backgroundColor: 'rgba(220, 38, 38, 0.08)',
                color: '#DC2626',
                borderColor: 'rgba(220, 38, 38, 0.25)',
              },
            }}
          >
            <CloseRoundedIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Main Full-Screen Body */}
      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          p: { xs: 2, sm: 3 },
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          WebkitOverflowScrolling: 'touch',
        }}
      >
        {/* Ticket Card Container */}
        <Box
          sx={{
            width: '100%',
            maxWidth: 440,
            backgroundColor: '#FFFFFF',
            borderRadius: 3.5,
            border: '1px solid #E2E8F0',
            boxShadow: '0 4px 20px rgba(0, 34, 85, 0.05)',
            overflow: 'hidden',
          }}
        >
          {/* Product Header & Value */}
          <Box
            sx={{
              p: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1.5,
            }}
          >
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0, flex: 1 }}>
              {productImage ? (
                <Avatar
                  src={productImage}
                  alt={productTitle}
                  variant="rounded"
                  sx={{
                    width: 44,
                    height: 44,
                    borderRadius: 2,
                    border: '1px solid #E2E8F0',
                    bgcolor: '#F8FAFC',
                    p: 0.4,
                    '& img': { objectFit: 'contain' },
                  }}
                />
              ) : (
                <Box
                  sx={{
                    width: 44,
                    height: 44,
                    borderRadius: 2,
                    bgcolor: 'rgba(0,34,85,0.06)',
                    color: '#002255',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <ConfirmationNumberOutlinedIcon sx={{ fontSize: 22 }} />
                </Box>
              )}

              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography
                  sx={{
                    fontSize: 14.5,
                    fontWeight: 700,
                    color: '#0F172A',
                    fontFamily: FONT_FAMILY,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    lineHeight: 1.3,
                  }}
                >
                  {productTitle}
                </Typography>

                {formattedExpiry && (
                  <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 0.25 }}>
                    <CalendarTodayOutlinedIcon sx={{ fontSize: 11, color: '#94A3B8' }} />
                    <Typography
                      sx={{
                        fontSize: 11,
                        fontWeight: 500,
                        color: '#64748B',
                        fontFamily: FONT_FAMILY,
                      }}
                    >
                      Valid till {formattedExpiry}
                    </Typography>
                  </Stack>
                )}
              </Box>
            </Stack>

            <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
              <Typography
                sx={{
                  fontSize: 9.5,
                  fontWeight: 600,
                  color: '#64748B',
                  textTransform: 'uppercase',
                  letterSpacing: 0.5,
                  lineHeight: 1,
                }}
              >
                Value
              </Typography>
              <Typography
                sx={{
                  fontSize: 18,
                  fontWeight: 800,
                  color: '#15803D',
                  fontFamily: FONT_FAMILY,
                  lineHeight: 1.2,
                }}
              >
                ₹{value}
              </Typography>
            </Box>
          </Box>

          {/* Perforated Divider with decorative side notches */}
          <Box sx={{ position: 'relative', px: 2 }}>
            <Box
              sx={{
                position: 'absolute',
                left: -8,
                top: '50%',
                transform: 'translateY(-50%)',
                width: 16,
                height: 16,
                borderRadius: '50%',
                backgroundColor: '#F8FAFC',
                border: '1px solid #CBD5E1',
                zIndex: 1,
              }}
            />
            <Divider
              sx={{
                borderStyle: 'dashed',
                borderColor: '#CBD5E1',
                borderWidth: 1,
              }}
            />
            <Box
              sx={{
                position: 'absolute',
                right: -8,
                top: '50%',
                transform: 'translateY(-50%)',
                width: 16,
                height: 16,
                borderRadius: '50%',
                backgroundColor: '#F8FAFC',
                border: '1px solid #CBD5E1',
                zIndex: 1,
              }}
            />
          </Box>

          {/* Voucher Credentials */}
          <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1.25 }}>
            {voucherNo && (
              <MobileCredentialBox
                label="Gift Card Number"
                value={voucherNo}
                type="code"
                copiedKey={copiedKey}
                onCopy={handleCopy}
              />
            )}

            {voucherPin && (
              <MobileCredentialBox
                label="Gift Card PIN"
                value={voucherPin}
                type="pin"
                copiedKey={copiedKey}
                onCopy={handleCopy}
              />
            )}

            {/* Barcode Display: Scaled to fit single view without horizontal scrolling */}
            {barcodeVal && (
              <Box
                sx={{
                  mt: 0.5,
                  pt: 1.5,
                  borderTop: '1px solid #F1F5F9',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  width: '100%',
                }}
              >
                <Box
                  sx={{
                    width: '100%',
                    py: 1,
                    px: 1.25,
                    bgcolor: '#FFFFFF',
                    borderRadius: 2,
                    border: '1px solid #E2E8F0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    overflow: 'hidden',
                    '& svg': {
                      maxWidth: '100% !important',
                      height: 'auto !important',
                      maxHeight: 46,
                      display: 'block',
                      margin: '0 auto',
                    },
                  }}
                >
                  <Barcode
                    value={barcodeVal}
                    height={40}
                    width={1.4}
                    fontSize={11}
                    margin={0}
                    displayValue={false}
                  />
                </Box>
                <Typography
                  variant="caption"
                  sx={{
                    mt: 0.75,
                    fontSize: 11,
                    fontWeight: 500,
                    color: '#64748B',
                    fontFamily: FONT_FAMILY,
                    textAlign: 'center',
                  }}
                >
                  * Scan barcode at counter or checkout to redeem
                </Typography>
              </Box>
            )}
          </Box>
        </Box>

        {/* Dismiss Button */}
        <Button
          fullWidth
          variant="outlined"
          size="medium"
          onClick={handleClose}
          sx={{
            mt: 2.5,
            maxWidth: 440,
            py: 1,
            borderRadius: 2,
            fontWeight: 600,
            fontSize: 13,
            color: '#002255',
            borderColor: '#CBD5E1',
            backgroundColor: '#FFFFFF',
            fontFamily: FONT_FAMILY,
            boxShadow: '0 1px 2px rgba(0,0,0,0.03)',
            '&:hover': {
              borderColor: '#002255',
              backgroundColor: 'rgba(0,34,85,0.04)',
            },
          }}
        >
          Back to Orders
        </Button>
      </Box>
    </Box>
  );
}

export default React.memo(MobileGiftVoucher);
